import { useEffect, useState } from "react";
import api from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { toast } from "sonner";
import { nfcWriteSupported, nfcWrite, nfcReadOnce, nfcErrorInfo, tokenFromUrl, nfcEndSession } from "../lib/nfc";
import {
  Broadcast, CheckCircle, XCircle, Copy, Printer, Prohibit, ArrowsClockwise, Spinner,
  WarningCircle, DeviceMobile, Tag as TagIcon, MapPin, QrCode,
} from "@phosphor-icons/react";
import { fmtDateTime } from "../lib/date";

const STATUS = {
  blank: { label: "Blank", cls: "bg-slate-100 text-slate-600" },
  assigned: { label: "Assigned", cls: "bg-blue-100 text-blue-700" },
  written: { label: "Written", cls: "bg-amber-100 text-amber-700" },
  verified: { label: "Verified", cls: "bg-emerald-100 text-emerald-700" },
  active: { label: "Active", cls: "bg-emerald-100 text-emerald-700" },
  revoked: { label: "Revoked", cls: "bg-red-100 text-red-700" },
  replaced: { label: "Replaced", cls: "bg-slate-200 text-slate-500" },
};

export function NfcStatusBadge({ status }) {
  const s = STATUS[status] || STATUS.assigned;
  return <span data-testid="nfc-status-badge" className={`inline-block text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded ${s.cls}`}>{s.label}</span>;
}

export function QrImage({ tagId, className = "w-40 h-40" }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let url;
    api.get(`/tags/${tagId}/qr`, { responseType: "blob" }).then((r) => { url = URL.createObjectURL(r.data); setSrc(url); }).catch(() => {});
    return () => url && URL.revokeObjectURL(url);
  }, [tagId]);
  return src ? <img src={src} alt="QR" className={className} data-testid={`qr-${tagId}`} /> : <div className={`bg-slate-100 ${className}`} />;
}

function TagFacts({ tag }) {
  const tgt = tag.target || {};
  return (
    <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-sm">
      <div className="font-body font-bold text-slate-900">{tgt.asset_name || tgt.label || "—"}</div>
      <div className="font-mono text-xs text-slate-500">{tgt.asset_number}{tgt.registration ? ` · ${tgt.registration}` : ""}</div>
      <div className="flex items-center gap-2 mt-1.5 flex-wrap">
        <span className="font-mono text-xs font-bold text-slate-700">{tag.tag_ref}</span>
        <NfcStatusBadge status={tag.status} />
        {tag.position && <span className="inline-flex items-center gap-1 text-xs text-slate-600"><MapPin size={12} weight="bold" />{tag.position}</span>}
      </div>
      <div className="font-mono text-[10px] text-slate-400 break-all mt-1.5">{tag.url}</div>
    </div>
  );
}

// Shared write + verify flow used by both the asset panel and bulk programming.
export function NfcProgrammeDialog({ tag: initialTag, open, onClose, onUpdated, onNext }) {
  const supported = nfcWriteSupported();
  const [tag, setTag] = useState(initialTag);
  const [step, setStep] = useState("ready");
  const [msg, setMsg] = useState("");
  const [mismatch, setMismatch] = useState(null);
  const [identify, setIdentify] = useState(null);

  useEffect(() => { setTag(initialTag); setStep("ready"); setMsg(""); setMismatch(null); setIdentify(null); if (!open) nfcEndSession(); }, [initialTag, open]);
  useEffect(() => () => nfcEndSession(), []);  // release the held NFC radio when the dialog unmounts

  const logFail = (error) => api.post(`/tags/${tag.id}/write-failed`, { error }).catch(() => {});

  const doWrite = async (overwrite) => {
    setStep("writing"); setMsg(""); setIdentify(null);
    try {
      await nfcWrite(tag.url, { overwrite });
      const r = await api.post(`/tags/${tag.id}/written`, {});
      setTag(r.data); onUpdated && onUpdated(r.data);
      setStep("written");
      toast.success("NFC tag programmed");
    } catch (e) {
      const info = nfcErrorInfo(e);
      logFail(info.message);
      if (info.kind === "cancelled") { setStep("ready"); toast.message("NFC programming cancelled"); return; }
      if (!overwrite && (info.kind === "failed" || info.kind === "disabled")) {
        // Likely the tag already contains data (overwrite=false rejected). Offer explicit overwrite.
        setMsg(info.message); setStep("overwrite"); return;
      }
      setMsg(info.message); setStep(info.kind === "unsupported" ? "unsupported" : "error");
    }
  };

  const identifyExisting = async () => {
    try {
      const { url } = await nfcReadOnce({});
      const tok = tokenFromUrl(url);
      if (!tok) { setIdentify({ recognised: false, unknown: true }); return; }
      const r = await api.get(`/tags/lookup?token=${encodeURIComponent(tok)}`);
      setIdentify(r.data);
    } catch (e) { toast.error(nfcErrorInfo(e).message); }
  };

  const doVerify = async () => {
    setStep("verifying"); setMismatch(null);
    try {
      const { url } = await nfcReadOnce({});
      const r = await api.post(`/tags/${tag.id}/verify`, { scanned_url: url, scanned_token: tokenFromUrl(url) });
      if (r.data.verified) {
        setTag(r.data); onUpdated && onUpdated(r.data); setStep("verified");
        toast.success("Tag verified & activated");
      } else {
        setMismatch(r.data); setStep("mismatch");
      }
    } catch (e) {
      setMsg(nfcErrorInfo(e).message); setStep("error");
    }
  };

  const copy = () => { navigator.clipboard.writeText(tag.url); toast.success("URL copied"); };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose && onClose()}>
      <DialogContent className="bg-white max-w-md" data-testid="nfc-programme-dialog" aria-describedby={undefined}>
        <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><Broadcast size={20} weight="bold" />Programme NFC Tag</DialogTitle></DialogHeader>
        <div className="flex flex-col gap-4">
          <TagFacts tag={tag} />

          {!supported && (
            <div data-testid="nfc-unsupported" className="text-center">
              <WarningCircle size={40} weight="fill" className="text-amber-500 mx-auto" />
              <p className="font-body text-sm text-slate-700 mt-2">NFC writing is not supported by this browser/device. Use QR instead.</p>
              <div className="flex flex-col items-center gap-2 mt-3">
                <QrImage tagId={tag.id} />
                <div className="flex gap-2">
                  <Button data-testid="nfc-copy-url" onClick={copy} className="bg-blue-600 font-semibold"><Copy size={16} weight="bold" className="mr-1" />Copy URL</Button>
                  <Button data-testid="nfc-show-qr" onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/tags/${tag.id}/qr`, "_blank")} className="bg-slate-700 font-semibold"><Printer size={16} weight="bold" className="mr-1" />Print QR</Button>
                </div>
              </div>
            </div>
          )}

          {supported && step === "ready" && (
            <>
              <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-4 text-center">
                <DeviceMobile size={40} weight="bold" className="text-blue-600 mx-auto" />
                <p className="font-display text-lg font-black text-slate-900 mt-1">Ready to programme NFC tag</p>
                <p className="font-body text-sm text-slate-600">Hold the blank NFC tag against the back of this phone.</p>
              </div>
              <div className="flex gap-2">
                <Button data-testid="nfc-write-btn" onClick={() => doWrite(false)} className="flex-1 h-14 bg-blue-600 hover:bg-blue-700 font-bold uppercase text-base">Write Tag</Button>
                <Button data-testid="nfc-cancel-btn" variant="outline" onClick={onClose} className="h-14 font-semibold">Cancel</Button>
              </div>
            </>
          )}

          {supported && (step === "writing" || step === "verifying") && (
            <div className="text-center py-6" data-testid="nfc-waiting">
              <Spinner size={44} weight="bold" className="text-blue-600 mx-auto animate-spin" />
              <p className="font-display text-lg font-black text-slate-900 mt-3 animate-pulse">
                {step === "writing" ? "Waiting for NFC tag…" : "Tap the programmed NFC tag again"}
              </p>
              <p className="font-body text-sm text-slate-500 mt-1">Hold the tag steady against the back of the phone.</p>
            </div>
          )}

          {supported && step === "overwrite" && (
            <div data-testid="nfc-overwrite" className="text-center">
              <WarningCircle size={40} weight="fill" className="text-amber-500 mx-auto" />
              <p className="font-display text-base font-black text-slate-900 mt-2">This NFC tag already contains data</p>
              <p className="font-body text-sm text-slate-600 mt-1">{msg}</p>
              {identify && (identify.recognised
                ? <p className="font-body text-sm text-amber-800 mt-2">Existing TMIC tag <b>{identify.tag_ref}</b>{identify.target?.label ? ` → ${identify.target.label}` : ""} ({identify.status}).</p>
                : <p className="font-body text-sm text-slate-600 mt-2">Contains unknown / non-TMIC data.</p>)}
              <div className="flex flex-col gap-2 mt-3">
                <Button data-testid="nfc-identify-btn" variant="outline" onClick={identifyExisting} className="font-semibold">Identify existing tag</Button>
                <Button data-testid="nfc-overwrite-btn" onClick={() => doWrite(true)} className="h-12 bg-amber-500 text-slate-900 hover:bg-amber-600 font-bold uppercase">Overwrite Tag</Button>
                <Button variant="ghost" onClick={() => setStep("ready")} className="font-semibold">Cancel</Button>
              </div>
            </div>
          )}

          {supported && step === "written" && (
            <div className="text-center" data-testid="nfc-written">
              <CheckCircle size={48} weight="fill" className="text-emerald-600 mx-auto" />
              <p className="font-display text-xl font-black text-emerald-700 uppercase mt-2">NFC Tag Programmed</p>
              <p className="font-body text-sm text-slate-600 mt-1">{tag.tag_ref} · written {tag.written_at ? fmtDateTime(tag.written_at) : "now"}</p>
              <p className="font-body text-xs text-amber-700 mt-1">Not active yet — verify the physical tag to activate it.</p>
              <div className="flex flex-col gap-2 mt-3">
                <Button data-testid="nfc-verify-btn" onClick={doVerify} className="h-14 bg-blue-600 hover:bg-blue-700 font-bold uppercase text-base">Verify Tag</Button>
                {onNext && <Button data-testid="nfc-another-btn" variant="outline" onClick={onNext} className="font-semibold">Programme Another</Button>}
                <Button data-testid="nfc-done-btn" variant="ghost" onClick={onClose} className="font-semibold">Done</Button>
              </div>
            </div>
          )}

          {supported && step === "verified" && (
            <div className="text-center" data-testid="nfc-verified">
              <CheckCircle size={48} weight="fill" className="text-emerald-600 mx-auto" />
              <p className="font-display text-xl font-black text-emerald-700 uppercase mt-2">Tag Verified</p>
              <p className="font-body text-sm text-slate-700 mt-1">{tag.target?.asset_name || tag.target?.label} · {tag.tag_ref}</p>
              <p className="font-body text-xs text-slate-500">Verified {tag.verified_at ? fmtDateTime(tag.verified_at) : "now"}{tag.verified_by_name ? ` by ${tag.verified_by_name}` : ""} — now Active.</p>
              <div className="flex flex-col gap-2 mt-3">
                {onNext && <Button data-testid="nfc-next-btn" onClick={onNext} className="h-12 bg-blue-600 hover:bg-blue-700 font-bold uppercase">Next Asset</Button>}
                <Button data-testid="nfc-finish-btn" onClick={onClose} className="h-12 bg-slate-900 font-bold uppercase">Done</Button>
              </div>
            </div>
          )}

          {supported && step === "mismatch" && (
            <div className="text-center" data-testid="nfc-mismatch">
              <XCircle size={44} weight="fill" className="text-red-600 mx-auto" />
              <p className="font-display text-lg font-black text-red-700 uppercase mt-2">Tag Does Not Match</p>
              <p className="font-body text-sm text-slate-600 mt-1">Read a tag but it points elsewhere.</p>
              <p className="font-body text-xs text-slate-500 mt-1">Expected {mismatch?.expected_tag_ref}{mismatch?.read_tag_ref ? ` · read ${mismatch.read_tag_ref}` : ""}</p>
              <div className="flex gap-2 mt-3">
                <Button data-testid="nfc-rewrite-btn" onClick={() => doWrite(true)} className="flex-1 h-12 bg-amber-500 text-slate-900 font-bold uppercase">Rewrite Tag</Button>
                <Button variant="outline" onClick={onClose} className="h-12 font-semibold">Cancel</Button>
              </div>
            </div>
          )}

          {supported && (step === "error" || step === "unsupported") && (
            <div className="text-center" data-testid="nfc-error">
              <WarningCircle size={44} weight="fill" className="text-red-500 mx-auto" />
              <p className="font-body text-sm text-slate-700 mt-2">{msg}</p>
              <div className="flex gap-2 mt-3 justify-center">
                {step === "error" && <Button data-testid="nfc-retry-btn" onClick={() => doWrite(false)} className="bg-blue-600 font-semibold">Try Again</Button>}
                <Button variant="outline" onClick={copy} className="font-semibold"><Copy size={16} weight="bold" className="mr-1" />Copy URL</Button>
                <Button variant="ghost" onClick={onClose} className="font-semibold">Close</Button>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

const REVOKE_REASONS = ["Lost", "Damaged", "Removed from asset", "Asset sold", "Incorrectly programmed", "Replaced", "Other"];

export function NfcReasonDialog({ open, onClose, onConfirm, title, confirmLabel, danger }) {
  const [reason, setReason] = useState("");
  const [note, setNote] = useState("");
  useEffect(() => { if (open) { setReason(""); setNote(""); } }, [open]);
  const final = reason === "Other" ? note.trim() : (note.trim() ? `${reason} — ${note.trim()}` : reason);
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white max-w-sm" data-testid="nfc-reason-dialog">
        <DialogHeader><DialogTitle className="font-display">{title}</DialogTitle></DialogHeader>
        <label className="font-body text-sm font-semibold">Reason</label>
        <select data-testid="nfc-reason-select" className="h-10 border-2 border-slate-300 rounded px-2" value={reason} onChange={(e) => setReason(e.target.value)}>
          <option value="">Select a reason…</option>{REVOKE_REASONS.map((r) => <option key={r} value={r}>{r}</option>)}
        </select>
        <Input data-testid="nfc-reason-note" placeholder={reason === "Other" ? "Describe the reason" : "Optional note"} value={note} onChange={(e) => setNote(e.target.value)} />
        <Button data-testid="nfc-reason-confirm" disabled={!reason || (reason === "Other" && !note.trim())} onClick={() => onConfirm(final)}
          className={`h-12 font-bold uppercase ${danger ? "bg-red-600 hover:bg-red-700" : "bg-amber-500 text-slate-900 hover:bg-amber-600"}`}>{confirmLabel}</Button>
      </DialogContent>
    </Dialog>
  );
}

// Fetch the server-generated QR PNG (auth'd) as a data URL so it can be embedded in a print window.
async function fetchQrDataUrl(tagId) {
  const r = await api.get(`/tags/${tagId}/qr`, { responseType: "blob" });
  return await new Promise((resolve) => { const fr = new FileReader(); fr.onload = () => resolve(fr.result); fr.readAsDataURL(r.data); });
}

// Open a minimal print window sized in millimetres (no app chrome / browser header where possible).
function printQrLabel({ dataUrl, wmm, hmm, layout, tag }) {
  const tgt = tag.target || {};
  const qrMm = Math.min(wmm, hmm);
  const isLabel = layout === "asset_label";
  const pageW = isLabel ? qrMm + 58 : wmm;
  const pageH = isLabel ? Math.max(qrMm, 30) : hmm;
  const win = window.open("", "_blank", "width=480,height=480");
  const labelText = isLabel ? `
      <div class="txt">
        <div class="num">${tgt.asset_number || ""}</div>
        <div class="name">${tgt.asset_name || tgt.label || ""}</div>
        ${tgt.registration ? `<div class="reg">${tgt.registration}</div>` : ""}
        <div class="ref">${tag.tag_ref}</div>
        <div class="inst">Scan for asset information</div>
      </div>` : "";
  win.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>${tag.tag_ref}</title>
    <style>
      @page { size: ${pageW}mm ${pageH}mm; margin: 0; }
      html,body { margin:0; padding:0; }
      .wrap { width:${pageW}mm; height:${pageH}mm; display:flex; align-items:center; gap:4mm; padding:${isLabel ? "2mm" : "0"}; box-sizing:border-box; font-family: Arial, sans-serif; }
      img { width:${qrMm}mm; height:${qrMm}mm; display:block; }
      .txt { display:flex; flex-direction:column; justify-content:center; }
      .num { font-size:10pt; font-weight:800; }
      .name { font-size:9pt; font-weight:600; }
      .reg { font-size:9pt; font-family: monospace; }
      .ref { font-size:7pt; font-family: monospace; color:#444; margin-top:1mm; }
      .inst { font-size:6pt; color:#666; }
    </style></head><body onload="setTimeout(function(){window.print();window.close();},250)">
    <div class="wrap"><img src="${dataUrl}" alt="QR"/>${labelText}</div></body></html>`);
  win.document.close();
}

const QR_SIZES = ["25 × 25 mm", "30 × 30 mm", "40 × 40 mm", "50 × 50 mm", "75 × 75 mm", "100 × 100 mm", "Custom"];
const PRINT_PREF_KEY = "tmic_qr_print";

export function QrPrintDialog({ tag, open, onClose, onPrinted }) {
  const saved = (() => { try { return JSON.parse(localStorage.getItem(PRINT_PREF_KEY) || "{}"); } catch { return {}; } })();
  const [size, setSize] = useState(saved.size || "40 × 40 mm");
  const [customW, setCustomW] = useState(saved.customW || 40);
  const [customH, setCustomH] = useState(saved.customH || 40);
  const [layout, setLayout] = useState(saved.layout || "qr_only");
  const [dataUrl, setDataUrl] = useState(null);

  useEffect(() => { if (open && tag) fetchQrDataUrl(tag.id).then(setDataUrl).catch(() => {}); }, [open, tag]);

  const dims = size === "Custom" ? { w: Number(customW) || 40, h: Number(customH) || 40 } : (() => { const n = parseInt(size); return { w: n, h: n }; })();
  const sizeLabel = size === "Custom" ? `${dims.w} × ${dims.h} mm` : size;

  const doPrint = async () => {
    if (!dataUrl) return;
    printQrLabel({ dataUrl, wmm: dims.w, hmm: dims.h, layout, tag });
    try { localStorage.setItem(PRINT_PREF_KEY, JSON.stringify({ size, customW, customH, layout })); } catch { /* ignore */ }
    try { const r = await api.post(`/tags/${tag.id}/qr-printed`, { size: sizeLabel, label_type: layout === "asset_label" ? "Asset Label" : "QR Only" }); onPrinted && onPrinted(r.data); } catch { /* ignore */ }
    toast.success("Sent to printer");
  };

  // Preview scale: mm→px at ~3px/mm for on-screen preview only.
  const px = (mm) => Math.round(mm * 3);
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white max-w-md" data-testid="qr-print-dialog" aria-describedby={undefined}>
        <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><Printer size={20} weight="bold" />Print QR — {tag?.tag_ref}</DialogTitle></DialogHeader>
        <div className="flex flex-col gap-3">
          <div>
            <label className="font-body text-sm font-semibold">Physical size</label>
            <select data-testid="qr-size" className="w-full h-10 border-2 border-slate-300 rounded px-2 mt-1" value={size} onChange={(e) => setSize(e.target.value)}>
              {QR_SIZES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          {size === "Custom" && (
            <div className="flex gap-2">
              <div className="flex-1"><label className="text-xs font-semibold text-slate-600">Width (mm)</label><Input data-testid="qr-custom-w" type="number" value={customW} onChange={(e) => setCustomW(e.target.value)} /></div>
              <div className="flex-1"><label className="text-xs font-semibold text-slate-600">Height (mm)</label><Input data-testid="qr-custom-h" type="number" value={customH} onChange={(e) => setCustomH(e.target.value)} /></div>
            </div>
          )}
          <div>
            <label className="font-body text-sm font-semibold">Layout</label>
            <div className="flex gap-2 mt-1">
              <Button data-testid="qr-layout-qr" variant={layout === "qr_only" ? "default" : "outline"} onClick={() => setLayout("qr_only")} className="flex-1 font-semibold">QR Only</Button>
              <Button data-testid="qr-layout-label" variant={layout === "asset_label" ? "default" : "outline"} onClick={() => setLayout("asset_label")} className="flex-1 font-semibold">Asset Label</Button>
            </div>
          </div>
          <div className="border-2 border-dashed border-slate-300 rounded-lg p-3">
            <div className="text-xs font-bold uppercase text-slate-400 mb-2">Print preview · {layout === "asset_label" ? `${sizeLabel} label` : `${sizeLabel} QR`}</div>
            <div data-testid="qr-preview" className="mx-auto bg-white border border-slate-200 flex items-center gap-2 p-1" style={{ width: layout === "asset_label" ? px(Math.min(dims.w, dims.h) + 58) : px(dims.w), minHeight: px(Math.min(dims.w, dims.h)) }}>
              {dataUrl ? <img src={dataUrl} alt="QR" style={{ width: px(Math.min(dims.w, dims.h)), height: px(Math.min(dims.w, dims.h)) }} /> : <div className="bg-slate-100" style={{ width: px(dims.w), height: px(dims.h) }} />}
              {layout === "asset_label" && (
                <div className="text-[10px] leading-tight">
                  <div className="font-bold">{tag?.target?.asset_number}</div>
                  <div>{tag?.target?.asset_name || tag?.target?.label}</div>
                  {tag?.target?.registration && <div className="font-mono">{tag.target.registration}</div>}
                  <div className="font-mono text-slate-500">{tag?.tag_ref}</div>
                </div>
              )}
            </div>
          </div>
          <Button data-testid="qr-print-btn" disabled={!dataUrl} onClick={doPrint} className="h-12 bg-slate-900 hover:bg-slate-800 font-bold uppercase"><Printer size={18} weight="bold" className="mr-1" />Print</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Unified hub shown after creating/opening a tag: NFC + QR outputs of the ONE tag record.
export function TagActionsDialog({ tag: initialTag, open, onClose, onUpdated }) {
  const [tag, setTag] = useState(initialTag);
  const [prog, setProg] = useState(false);
  const [qr, setQr] = useState(false);
  const [thenPrint, setThenPrint] = useState(false);
  useEffect(() => { setTag(initialTag); }, [initialTag, open]);
  const update = (t) => { setTag(t); onUpdated && onUpdated(t); };
  const copy = () => { navigator.clipboard.writeText(tag.url); toast.success("Tag URL copied"); };
  return (
    <>
      <Dialog open={open && !prog && !qr} onOpenChange={(o) => !o && onClose()}>
        <DialogContent className="bg-white max-w-md" data-testid="tag-actions-dialog" aria-describedby={undefined}>
          <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><TagIcon size={20} weight="bold" />{tag?.tag_ref}</DialogTitle></DialogHeader>
          <TagFacts tag={tag} />
          <div className="grid grid-cols-2 gap-2 mt-1">
            <Button data-testid="ta-write-nfc" onClick={() => setProg(true)} className="bg-blue-600 hover:bg-blue-700 font-bold uppercase"><Broadcast size={16} weight="bold" className="mr-1" />Write NFC</Button>
            <Button data-testid="ta-print-qr" onClick={() => setQr(true)} className="bg-slate-900 font-bold uppercase"><Printer size={16} weight="bold" className="mr-1" />Print QR</Button>
            <Button data-testid="ta-nfc-and-qr" onClick={() => { setThenPrint(true); setProg(true); }} className="col-span-2 bg-blue-700 font-bold uppercase"><Broadcast size={16} weight="bold" className="mr-1" />Write NFC + Print QR</Button>
            <Button data-testid="ta-show-qr" variant="outline" onClick={() => setQr(true)} className="font-semibold"><QrCode size={16} weight="bold" className="mr-1" />Show QR</Button>
            <Button data-testid="ta-copy-url" variant="outline" onClick={copy} className="font-semibold"><Copy size={16} weight="bold" className="mr-1" />Copy URL</Button>
            <Button data-testid="ta-done" variant="ghost" onClick={onClose} className="col-span-2 font-semibold">Done</Button>
          </div>
        </DialogContent>
      </Dialog>
      {prog && <NfcProgrammeDialog tag={tag} open={prog} onClose={() => { setProg(false); if (thenPrint) { setThenPrint(false); setQr(true); } }} onUpdated={update} />}
      {qr && <QrPrintDialog tag={tag} open={qr} onClose={() => setQr(false)} onPrinted={update} />}
    </>
  );
}


export function AssetNfcPanel({ assetId, canManage, canReplaceRevoke, onAddTag }) {
  const [tags, setTags] = useState([]);
  const [prog, setProg] = useState(null);
  const [revoke, setRevoke] = useState(null);
  const [replace, setReplace] = useState(null);
  const [qrFor, setQrFor] = useState(null);
  const [printFor, setPrintFor] = useState(null);

  const load = () => api.get(`/tags?target_id=${assetId}`).then((r) => setTags(r.data)).catch(() => {});
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [assetId]);

  const openProgramme = async (t) => { const r = await api.get(`/tags/${t.id}`); setProg(r.data); };
  const doRevoke = async (reason) => {
    try { await api.post(`/tags/${revoke.id}/revoke`, { reason }); toast.success("Tag revoked"); setRevoke(null); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const doReplace = async (reason) => {
    try { const r = await api.post(`/tags/${replace.id}/replace`, { reason }); toast.success("Replacement issued — programme & verify it"); setReplace(null); load(); setProg(r.data); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div data-testid="asset-nfc-panel">
      <div className="flex items-center justify-between mb-2">
        <span className="font-body font-semibold text-slate-800 inline-flex items-center gap-2"><Broadcast size={18} weight="bold" />NFC / QR Tags</span>
        {canManage && onAddTag && <Button data-testid="nfc-add-tag" onClick={onAddTag} className="h-9 bg-slate-900 font-semibold"><TagIcon size={16} weight="bold" className="mr-1" />Add Tag</Button>}
      </div>
      {tags.length ? tags.map((t) => (
        <div key={t.id} data-testid={`asset-tag-${t.id}`} className={`bg-white border-2 rounded-lg p-3 mb-2 ${t.is_active ? "border-slate-200" : "border-red-200 opacity-70"}`}>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-mono text-xs font-bold text-slate-700">{t.tag_ref}</span>
            <NfcStatusBadge status={t.status} />
            {t.position && <span className="inline-flex items-center gap-1 text-xs text-slate-600"><MapPin size={12} weight="bold" />{t.position}</span>}
            <span className="ml-auto font-mono text-[10px] text-slate-400">Last scan {t.last_scan_at ? fmtDateTime(t.last_scan_at) : "never"}</span>
          </div>
          <div className="flex flex-wrap gap-x-4 gap-y-0.5 mt-1 text-[11px] font-body">
            <span className={t.verified_at ? "text-emerald-700" : t.written_at ? "text-amber-700" : "text-slate-400"}>
              NFC: {t.verified_at ? "✓ Written & Verified" : t.written_at ? "Written (unverified)" : "not written"}
            </span>
            <span className={t.qr_print_count > 0 ? "text-emerald-700" : "text-slate-400"}>
              QR: {t.qr_print_count > 0 ? `✓ Printed${t.last_qr_print_size ? ` — ${t.last_qr_print_size}` : ""}${t.qr_print_count > 1 ? ` ×${t.qr_print_count}` : ""}` : "not printed"}
            </span>
          </div>
          {(t.replaces_tag_ref || t.replaced_by_tag_ref || t.revoke_reason) && (
            <div className="font-body text-[11px] text-slate-500 mt-1">
              {t.replaces_tag_ref && <span>Replaces {t.replaces_tag_ref}. </span>}
              {t.replaced_by_tag_ref && <span>Replaced by {t.replaced_by_tag_ref}. </span>}
              {t.revoke_reason && <span>Revoked: {t.revoke_reason}.</span>}
            </div>
          )}
          <div className="flex flex-wrap gap-2 mt-2">
            <Button data-testid={`nfc-test-${t.id}`} variant="outline" size="sm" className="h-8 text-xs" onClick={() => window.open(t.url, "_blank")}>Test</Button>
            <Button data-testid={`nfc-showqr-${t.id}`} variant="outline" size="sm" className="h-8 text-xs" onClick={() => setQrFor(t)}><QrCode size={14} weight="bold" className="mr-1" />Show QR</Button>
            {canManage && <Button data-testid={`nfc-printqr-${t.id}`} variant="outline" size="sm" className="h-8 text-xs" onClick={() => setPrintFor(t)}><Printer size={14} weight="bold" className="mr-1" />{t.qr_print_count > 0 ? "Reprint QR" : "Print QR"}</Button>}
            {canManage && t.is_active && (t.status === "assigned" || t.status === "written") &&
              <Button data-testid={`nfc-write-${t.id}`} size="sm" className="h-8 text-xs bg-blue-600 font-semibold" onClick={() => openProgramme(t)}><Broadcast size={14} weight="bold" className="mr-1" />{t.status === "written" ? "Write / Verify" : "Write NFC"}</Button>}
            {canManage && t.is_active && t.status === "active" &&
              <Button data-testid={`nfc-rewrite-${t.id}`} variant="outline" size="sm" className="h-8 text-xs" onClick={() => openProgramme(t)}><Broadcast size={14} weight="bold" className="mr-1" />Rewrite NFC</Button>}
            {canReplaceRevoke && t.is_active &&
              <Button data-testid={`nfc-replace-${t.id}`} variant="outline" size="sm" className="h-8 text-xs text-amber-700" onClick={() => setReplace(t)}><ArrowsClockwise size={14} weight="bold" className="mr-1" />Replace</Button>}
            {canReplaceRevoke && t.is_active &&
              <Button data-testid={`nfc-revoke-${t.id}`} variant="outline" size="sm" className="h-8 text-xs text-red-600" onClick={() => setRevoke(t)}><Prohibit size={14} weight="bold" className="mr-1" />Revoke</Button>}
          </div>
        </div>
      )) : <p className="text-slate-400 text-sm p-2">No tags on this asset.</p>}

      {prog && <NfcProgrammeDialog tag={prog} open={!!prog} onClose={() => { setProg(null); load(); }} onUpdated={() => {}} />}
      {printFor && <QrPrintDialog tag={printFor} open={!!printFor} onClose={() => { setPrintFor(null); load(); }} onPrinted={() => load()} />}
      <NfcReasonDialog open={!!revoke} onClose={() => setRevoke(null)} onConfirm={doRevoke} title={`Revoke ${revoke?.tag_ref || "tag"}`} confirmLabel="Revoke Tag" danger />
      <NfcReasonDialog open={!!replace} onClose={() => setReplace(null)} onConfirm={doReplace} title={`Replace ${replace?.tag_ref || "tag"}`} confirmLabel="Create Replacement" />
      <Dialog open={!!qrFor} onOpenChange={(o) => !o && setQrFor(null)}>
        <DialogContent className="bg-white max-w-xs text-center" data-testid="nfc-qr-dialog">
          <DialogHeader><DialogTitle className="font-display">{qrFor?.tag_ref}</DialogTitle></DialogHeader>
          {qrFor && <>
            <div className="flex justify-center"><QrImage tagId={qrFor.id} /></div>
            <div className="font-mono text-[10px] text-slate-500 break-all">{qrFor.url}</div>
            <Button onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/tags/${qrFor.id}/qr`, "_blank")} className="bg-slate-700 font-semibold"><Printer size={16} weight="bold" className="mr-1" />Print QR Label</Button>
          </>}
        </DialogContent>
      </Dialog>
    </div>
  );
}
